import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthDebug } from '@/hooks/useAuthDebug';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';

export const AuthDebugPanel = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user, session, isReady, error, debugInfo, refreshAuth } = useAuthDebug();

  if (import.meta.env.PROD) {
    return null; // Don't show in production
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md">
      <Card className="bg-background/95 backdrop-blur-sm border-2 border-orange-500">
        <CardHeader 
          className="pb-2 cursor-pointer" 
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm text-orange-600">
              Auth Debug
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant={user ? "default" : "destructive"}>
                {user ? "Authenticated" : "Not Authenticated"}
              </Badge>
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </div>
        </CardHeader>
        
        {isExpanded && (
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <strong>Ready:</strong> {isReady ? "✅" : "⏳"}
              </div>
              <div>
                <strong>User ID:</strong> {user?.id ? user.id.substring(0, 8) + "..." : "None"}
              </div>
              <div>
                <strong>Email:</strong> {user?.email || "None"}
              </div>
              <div>
                <strong>Session:</strong> {session ? "✅" : "❌"}
              </div>
            </div>
            
            {error && (
              <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                <strong>Error:</strong> {error}
              </div>
            )}
            
            {debugInfo && (
              <details className="text-xs">
                <summary className="cursor-pointer font-medium">Debug Info</summary>
                <pre className="mt-2 p-2 bg-gray-50 rounded overflow-x-auto text-[10px]">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </details>
            )}
            
            <Button 
              size="sm" 
              variant="outline" 
              onClick={refreshAuth}
              className="w-full"
            >
              <RefreshCw size={12} className="mr-1" />
              Refresh Auth
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
};